import React, { useState, useEffect, useRef, UIEvent } from "react";
import styles from "./styles.module.scss";

import { Icon } from "@cobasi/ds-icons";
import ChevronLeft from "@cobasi/ds-icons/regular/chevronLeft";
import ChevronRight from "@cobasi/ds-icons/regular/chevronRight";

import { useWindowSize } from "@/hooks/useWindowSize";

type GalleryProps = {
  // currentActiveSlide?: (value: number) => void;
  currentActiveSlide?: any;
};

type CarouselFlexProps = {
  children: React.ReactNode;
  fullWidth?: boolean;
  showArrows?: boolean;
  showDots?: boolean;
  autoPlay?: boolean;
  speed?: number;
  prevArrow?: React.ReactNode;
  nextArrow?: React.ReactNode;
  elementsInScreen?: boolean;
  className?: string;
  classNameArrow?: string;
  hasPadding?: boolean;
} & GalleryProps;

const REFRESH = 56;

const CarouselFlex = ({
  children,
  fullWidth = false,
  showArrows = false,
  showDots = false,
  autoPlay = false,
  elementsInScreen = false,
  speed = 2000,
  prevArrow,
  nextArrow,
  currentActiveSlide,
  className = "",
  classNameArrow = "",
  hasPadding = false
}: CarouselFlexProps) => {
  const numItems = React.Children.count(children);
  const container = useRef<HTMLDivElement>(null);
  const [activeSlide, setActiveSlide] = useState(0);
  const [isScrolling, setIsScrolling] = useState<any>();
  const [arrowPrevDisabled, setArrowPrevDisabled] = useState(true);
  const [arrowNextDisabled, setArrowNextDisabled] = useState(false);
  const [hasScrollToShowArrow, setHasScrollToShowArrow] = useState(false);
  const { mobile } = useWindowSize();

  useEffect(() => {
    if (autoPlay && fullWidth) {
      setTimeout(() => {
        autoScroll(container.current);
      }, 1200);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoPlay, fullWidth]);

  useEffect(() => {
    if (container.current) {
      container.current.scrollWidth > container.current.clientWidth &&
        setHasScrollToShowArrow(true);
    }
  }, [numItems]);

  const onScroll = (event: UIEvent<HTMLDivElement>) => {
    isScrolling && window.clearTimeout(isScrolling);

    setIsScrolling(setTimeout(() => scrollStop(event), REFRESH + 100));
  };

  const scrollStop = (event: UIEvent<HTMLDivElement>) => {
    const elementInView = findElementIsInTheView(event?.target);
    const target = event.target as HTMLDivElement;
    const endScroll =
      Number(target.scrollLeft.toFixed(0)) + target.offsetWidth >=
      target.scrollWidth;
    setArrowPrevDisabled(false);
    setArrowNextDisabled(false);

    if (elementInView[0]?.index + 1 === numItems || endScroll) {
      return setArrowNextDisabled(true);
    }

    if (elementInView[0]?.index === 0) {
      return setArrowPrevDisabled(true);
    }

    if (container?.current && showDots) {
      const width = container?.current?.offsetWidth;
      const scrollLeft = container?.current?.scrollLeft;
      const sliderIndex = Math.round(scrollLeft / width);

      if (mobile && !elementsInScreen) {
        setActiveSlide(sliderIndex);
      } else {
        setActiveSlide(elementInView[0]?.index);
      }
      return !!currentActiveSlide && currentActiveSlide(sliderIndex);
    }
  };

  const goToSlide = (index: number) => {
    setActiveSlide(index);

    const childs = container.current;
    goToCurrentSlide(childs, index);
  };

  const prevScroll = (container: any) => {
    const elementInView = findElementIsInTheView()[0];
    const prevItem =
      container?.childNodes[elementInView?.index]?.previousElementSibling;

    if (prevItem) {
      setActiveSlide(elementInView?.index - 1);

      prevItem.scrollIntoView({ block: "nearest", inline: "start" });
    }
  };

  const nextScroll = (container: any) => {
    const elementInView = findElementIsInTheView()[0];
    const nextItem =
      container?.childNodes[elementInView?.index]?.nextElementSibling;

    if (nextItem) {
      setActiveSlide(elementInView?.index + 1);

      nextItem.scrollIntoView({ block: "nearest", inline: "start" });
    }
  };

  const goToCurrentSlide = (parent: any, index: number) => {
    parent.childNodes[index].scrollIntoView({
      block: "nearest",
      inline: "start"
    });
  };

  const iterateChilds = (parent: any) => {
    const elementInTheView: any = [];

    parent.childNodes.forEach((item: any, index: boolean) => {
      isVisible(item, container.current) &&
        elementInTheView.push({ item, index });
    });

    return elementInTheView;
  };

  const findElementIsInTheView = (target?: EventTarget) => {
    const childs = target ?? container.current;

    return iterateChilds(childs);
  };

  const autoScroll = (content: any) => {
    // const handlerScroll =
    setInterval(() => {
      if (content.scrollLeft + content.offsetWidth < content.scrollWidth) {
        const childSize = content.childNodes[0].scrollWidth;
        content.scrollBy(childSize, 0);
      }

      if (content.scrollLeft + content.offsetWidth === content.scrollWidth) {
        content.scrollBy({
          top: 0,
          left: -content.scrollWidth,
          behavior: "instant"
        });
        // clearInterval(handlerScroll);
      }
    }, speed);
  };

  const isVisible = (ele: HTMLElement, wrapper: any) => {
    const { left, right, width } = ele.getBoundingClientRect();
    const containerRect = wrapper.getBoundingClientRect();

    if (left >= containerRect.left && right <= containerRect.right) {
      return true;
    }

    if (right >= containerRect.right && width <= containerRect.width) {
      return true;
    }

    // checking for partial visibility
    // if (left < containerRect.width && right >= 0) {
    //   console.log("Element is partially visible in screen", ele);
    // }

    // return right <= containerRect.right
    //   ? containerRect.right - right <= width
    //   : left - containerRect.left <= width;
  };

  return (
    <div
      className={`${styles.carousel} ${showDots ? `${styles["with-dots"]}` : ""} ${hasScrollToShowArrow && hasPadding ? `${styles["padding-carousel"]}` : ""}  ${className}`}
    >
      {showArrows && hasScrollToShowArrow && (
        <>
          <button
            className={`${styles.arrow} ${styles["arrow-prev"]} arrow-prev ${classNameArrow ? classNameArrow : ""}`}
            id="prev"
            disabled={arrowPrevDisabled}
            onClick={() => prevScroll(container.current)}
            aria-label="Anterior"
          >
            {prevArrow ?? (
              <Icon icon={ChevronLeft} width="1.6rem" height="1.6rem" />
            )}
          </button>
          <button
            className={`${styles.arrow} ${styles["arrow-next"]} arrow-next ${classNameArrow ? classNameArrow : ""}`}
            id="next"
            disabled={arrowNextDisabled}
            onClick={() => nextScroll(container.current)}
            aria-label="Próximo"
          >
            {nextArrow ?? (
              <Icon icon={ChevronRight} width="1.6rem" height="1.6rem" />
            )}
          </button>
        </>
      )}
      <div
        ref={container}
        className={`${styles.wrapper} ${fullWidth && `${styles.full}`}`}
        onScroll={onScroll}
      >
        {React.Children.map(children, (child, index) => (
          <div key={index}>{child}</div>
        ))}
      </div>
      {showDots && (
        <div className={`${styles.dots} dots`}>
          <ul className={styles["dots-list"]}>
            {Array.from(Array(numItems).keys()).map((item, index: number) => (
              <li
                key={index}
                className={`${styles["dots-item"]} dotItem ${activeSlide === index && `${styles.active} dotItem-active`}`}
                onClick={
                  activeSlide === index ? () => true : () => goToSlide(index)
                }
              >
                <span>{item + 1}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

export default CarouselFlex;
